
import { Component, OnInit, QueryList, ViewChildren } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { SHARED_COMPONENTS } from '../../../../shared/shared-imports';
import { customValidator } from '../../../../Utils/validator';
import { ActivatedRoute, Router } from '@angular/router';
import { RoutesPath } from '../../../../shared/constants/routes-path';
import { SharedService } from '../../../../shared/services/shared.service';
import { debounceTime, distinctUntilChanged, filter, finalize, Subject, switchMap, tap } from 'rxjs';
import { Utils } from '../../../../Utils/utils';
import { ROLES } from './models/roles.model';
import { LinkMemberRequest } from '../../../../shared/models/services-request/link-member.model';
import { DocumentServiceType, FieldValue, ServiceName, SrIOName } from '../../../../shared/constants/services.enum';
import { ApiEndPoints, ApiRoot } from '../../../../shared/constants/api-endpoints';
import { ApiService } from '../../../../core/services/api.service';
import { LinkMemberQueryRequestModel, UpsertRequestModel } from './models/link-member-query-request.model';
import { SrStatus } from '../../../../shared/constants/status.enum';
import { AccountUser } from '../../../../shared/models/account-user.model';
import { SkeletonLoaderComponent } from "../../../../shared/components/skeleton-loader/skeleton-loader.component";
import { FileComponent } from '../../../../shared/components/controls/file/file.component';
import { DcMethodValues } from '../../../../shared/constants/dc-method.constants';

@Component({
  selector: 'app-add-business',
  imports: [ReactiveFormsModule, FormsModule, TranslateModule, ...SHARED_COMPONENTS, SkeletonLoaderComponent],
  templateUrl: './add-business.component.html',
  styleUrl: './add-business.component.scss'
})
export class AddBusinessComponent implements OnInit {
  @ViewChildren('fileCmp') fileComponents!: QueryList<FileComponent>;
  form: FormGroup;
  documentServiceType = DocumentServiceType;
  roles: any = ROLES;
  lang: string = 'en';
  loginUserDetail: any = "";
  userLoginName: string = "";
  srNumber: string = "";
  userCSN: string = "";
  userType: string = "";
  otpText: string = "";
  otpErrorKey: string | null = null;
  srStatus = SrStatus;
  isPrimaryUser: boolean = false;
  srInprgoressAwating: boolean = false;
  showNotes: boolean = false;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private sharedService: SharedService,
    private api: ApiService
  ) {
    this.lang = this.sharedService.getCurrentLang() || 'en';
    this.loginUserDetail = this.sharedService.getUserProfile();
    this.userLoginName = this.sharedService.getUserProfile()['Login Name'];
    this.userCSN = this.sharedService.getCsnAccountId('csn');
    this.userType = this.sharedService.getPrimaryAccount()?.['Account Type Code'];

    this.form = this.fb.group({
      company: [null, [Validators.required]],
      roles: this.fb.array(
        ROLES.map(() => this.fb.control(false)), // default unchecked
        [Utils.atLeastOneSelectedValidator]  // custom validator
      ),
      email: [{ value: this.loginUserDetail['EMail Addr'], disabled: true }, [Validators.required, customValidator('email')]],
      countryCode: [{ value: Utils.parseMobile(this.loginUserDetail['Phone #'], 'cc'), disabled: true }, [Validators.required]],
      phone: [{ value: Utils.parseMobile(this.loginUserDetail['Phone #'], 'number'), disabled: true }, [Validators.required, customValidator('mobile')]],
      officePhoneCountryCode: [null, [Validators.required]],
      officePhone: ['', [Validators.required, customValidator('uaePhoneNumber')]]
    });
    if (!this.userCSN) {
      this.sharedService.toastMessage('INFO', 'NO_CSN_NUMBER', 'info');
      this.router.navigate([RoutesPath.DASHBOARD]);
      return;
    }
    // SR Number in the url
    this.route.params.subscribe(params => {
      if (params['sr_no']) {
        this.srNumber = params['sr_no'];
        this.getSRDetail();
      }
    });

    if (sessionStorage.getItem("srNumber") && this.srNumber == "") {
      let storeSrNumber: any = sessionStorage.getItem("srNumber");
      this.srNumber = storeSrNumber;
      this.getSRDetail();
    }
  }

  selectedCompany: any = ""
  members: any = [];
  preload: any = [];
  showActions: boolean = true;
  loadingMember: boolean = false;
  private search$ = new Subject<string>();
  srFolder: any = null;
  mainFromSectionDocs: any[] = [];

  // This method is used to handle the on init lifecycle hook
  ngOnInit() {
    this.openFolder();
    //Search for company/member
    this.search$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        tap((term: any) => {
          // ≤2 chars or cleared ⇒ just show preload
          if (term?.searchText && term?.searchText.length <= 2) {
            this.members = this.preload;
          }
        }),
        filter((term) => term?.searchText && term?.searchText.length >= 2),
        tap(() => (this.loadingMember = true, this.members = [])),
        switchMap((term: any) =>
          this.getMembers(term.searchText, term.searchBy)
            .pipe(finalize(() => (this.loadingMember = false)))
        )
      )
      .subscribe((response: any) => {
        if (response && response['Error Code'] == "" && response['SiebelMessage']["DC Account Thin"]) {
          let memberData = Utils.checkIsArray(response['SiebelMessage']["DC Account Thin"]);

          this.members = memberData.map((item: any) => ({
            ...item,
            name: item["DC Name English"],
            description: item["DC Name Arabic"],
            Name: item["DC Name English"] || item["DC Name Arabic"] ? (this.lang === 'en' ? item["DC Name English"] : item["DC Name Arabic"]) : item["Account Number"],
            Value: item["Account Number"],
            memberNumber: item["CSN"] || item["Account Number"],
            licenseNumber: item["DC License Registration Number"]
          }));

          this.preload = this.members;
        } else {
          this.members = [];
        }
      });

    this.rolesFormArray.valueChanges.subscribe((selectedFlags: boolean[]) => {
      this.applyRoleSelectionRules(selectedFlags);
    });
    this.applyRoleSelectionRules(this.rolesFormArray.value as boolean[]);
  }

  // This method is used to get selected member using name
  getSelectedMember(name: string = "") {
    if (!name) {
      return;
    }
    this.getMembers(name, 'CSN')
      .pipe(finalize(() => (this.loadingMember = false)))
      .subscribe((response: any) => {
        if (response['SiebelMessage']["DC Account Thin"]) {
          let memberData = Utils.checkIsArray(response['SiebelMessage']["DC Account Thin"]);

          this.members = memberData.map((item: any) => ({
            ...item,
            name: item["DC Name English"],
            description: item["DC Name Arabic"],
            Name: item["DC Name English"] || item["DC Name Arabic"] ? (this.lang === 'en' ? item["DC Name English"] : item["DC Name Arabic"]) : item["Account Number"],
            Value: item["Account Number"]
          }));

          this.preload = this.members;
        }
      });

  }
  // This method is used to set company selected using data
  setCompanySelected(data: any) {
    this.selectedCompany = {
      nameEnglish: data[0]["DC Name English"],
      nameArabic: data[0]["DC Name Arabic"],
      CSN: data[0]["Account Number"]
    }
    this.form.get('company')?.setValue(this.selectedCompany);
    this.form.get('company')?.updateValueAndValidity();
  }
  /* receives the search term from the child */
  // This method is used to handle search using term
  handleSearch(term: string) {
    this.search$.next(term);
  }
  // This method is used to get members using term and search by
  getMembers(term: string = "", searchBy: string = "") {
    return this.sharedService.getDCAllMembers(term, searchBy);
  }
  // This method is used to roles form array
  get rolesFormArray(): FormArray {
    return this.form.get('roles') as FormArray;
  }

  private applyRoleSelectionRules(selectedFlags: boolean[]): void {
    const selectedRoleValues = this.getSelectedRoleValues(selectedFlags);
    const hasOwnerOrPartnerOrManager =
      selectedRoleValues.includes('Owner') ||
      selectedRoleValues.includes('Partner') ||
      selectedRoleValues.includes('Manager');

    const representativeIndex = this.roles.findIndex(
      (role: any) => role?.value === 'Representative'
    );
    if (representativeIndex < 0) {
      return;
    }

    const representativeControl = this.rolesFormArray.at(representativeIndex);
    if (!representativeControl) {
      return;
    }

    if (hasOwnerOrPartnerOrManager) {
      if (representativeControl.value) {
        representativeControl.setValue(false, { emitEvent: false });
      }
      representativeControl.disable({ emitEvent: false });
      return;
    }

    if (representativeControl.disabled) {
      representativeControl.enable({ emitEvent: false });
    }
  }

  private getSelectedRoleValues(selectedFlags: boolean[]): string[] {
    if (!Array.isArray(selectedFlags)) {
      return [];
    }
    return selectedFlags
      .map((checked, index) => (checked ? this.roles?.[index]?.value : null))
      .filter((value): value is string => !!value);
  }
  formSubmitLoader: boolean = false;
  isSaved: boolean = false;
  requestDetail: any = "";
  technicalErrorMsg: boolean = false;
  loadingSrDetail: boolean = false;
  showApproveAction: boolean = false;
  keepCompanyDisabled: boolean = false;
  // This method is used to get sr detail
  getSRDetail() {
    this.loadingSrDetail = true;
    const request = new LinkMemberQueryRequestModel();
    request.body.DCMethod = "QUERY";
    request.body.DCLoginName = this.userLoginName;
    request.body.DCLanguage = this.lang == 'ar' ? 'ARA' : 'ENU';
    request.body.DCIncomingHierarchy.MessageId = Utils.generateUniqueId();

    let srData = request.body.DCIncomingHierarchy["ListOfDC Community Portal SR IO"]["DC Community Portal SR"]
    srData['SR Number'] = this.srNumber;
    srData["SR Type"] = ServiceName.USER_MANAGEMENT;
    srData["Sub Type"] = ServiceName.USER_ACCESS;
    srData["CSN"] = this.userCSN;
    this.api.post(
      ApiRoot.ATA_DC_SERVICE_REQUEST_INTEGRATION_API, // Use the correct API root for Link Member
      ApiEndPoints.ATA_QUERY_SERVICEREQUEST,
      request
    ).subscribe({
      // This method is used to handle the next callback using response
      next: (response: any) => {
        if (response['Error Code'] == null && response["DCSRNumber"] && response.SiebelMessage?.["DC Community Portal SR"]) {
          let srDetail = response.SiebelMessage?.["DC Community Portal SR"];
          this.userCSN = srDetail?.["DC Member Num"] || this.userCSN;
          this.loadingSrDetail = false;
          this.requestDetail = response.SiebelMessage?.["DC Community Portal SR"];
          let roles: string[] = [];
          if (srDetail['DC Link Mem Answer1'] === "Y") {
            roles.push("Owner");
          }
          if (srDetail['DC Link Mem Answer2'] === "Y") {
            roles.push("Manager");
          }
          if (srDetail['DC Link Mem Answer3'] === "Y") {
            roles.push("Representative");
          }
          if (roles.length > 0) {
            this.patchRoles(roles);
          }
          this.getSelectedMember(srDetail["DC Link CSN"]);
          this.form.get("company")?.setValue(srDetail["DC Link CSN"]);
          if (srDetail["DC Created User Email"]) {
            this.form.get("email")?.setValue(srDetail["DC Created User Email"]);
          }
          if (srDetail["DC Created User Phone Number"]) {
            this.form.get("countryCode")?.setValue(Utils.parseMobile(srDetail["DC Created User Phone Number"], 'cc'));
            this.form.get("phone")?.setValue(Utils.parseMobile(srDetail["DC Created User Phone Number"], 'number'));
          }

          if(srDetail['DC Holder Mobile']) {
            this.form.get("officePhoneCountryCode")?.setValue(Utils.parseMobile(srDetail['DC Holder Mobile'], 'cc'));
            this.form.get("officePhone")?.setValue(Utils.parseMobile(srDetail['DC Holder Mobile'], 'number'));
          }

          if (srDetail['DC Link CSN'] == this.userCSN && this.userLoginName != srDetail['DC Link UserID']) {
            this.showApproveAction = true;
          }

          if (this.sharedService.enableNotesOption(srDetail["Status"])) {
            this.srInprgoressAwating = true;
            this.showNotes = true;
          }

          const companyControl = this.form.get('company');
          if (srDetail["Status"] != SrStatus.Draft) {
            this.keepCompanyDisabled = true;
            companyControl?.disable({ emitEvent: false });
          } else {
            this.keepCompanyDisabled = false;
          }

          if (srDetail["Status"] != SrStatus.Draft && srDetail["Status"] != SrStatus.AwaitingClarification && srDetail["Status"] != SrStatus.Open) {
            this.showActions = false;
            this.form.disable(); // disables all fields in the form
          }
        } else {
          this.loadingSrDetail = false;
          if (response['Error Code'] == 'DC-VAL-ERR' && response["Error Message"] != null) {
            this.errorMsg = response['Error Message'];
          } else {
            this.technicalErrorMsg = true;
          }
        }
      },
      error: () => {
        this.loadingSrDetail = false;
        this.technicalErrorMsg = true;
      }
    })
  }

  // This method is used to patch roles using selected roles
  patchRoles(selectedRoles: string[]) {
    const rolesArray = this.form.get('roles') as FormArray;
    rolesArray.controls.forEach((control, i) => {
      const roleValue = ROLES[i].value;
      // Mark checkbox as checked if role exists in selectedRoles
      if (selectedRoles.includes(roleValue)) {
        control.setValue(true);
      } else {
        control.setValue(false);
      }
    });
  }
  // This method is used to create a draft SR first, then submit the SR
  errorMsg: string = "";
  saveDraftOrSubmitFormData() {
    this.errorMsg = "";
    if (this.form.invalid) { this.form.markAllAsTouched(); return; };

    if (this.userCSN == this.form.getRawValue().company) {
      this.sharedService.toastMessage('info', 'SELECTED_MEMBER_EXIST', 'error', true)
      return;
    }
    this.upsertLinkMemberSr(true);
  }

  private upsertLinkMemberSr(isActionSubmit: boolean): void {
    this.errorMsg = "";
    const defaultShowActions = this.showActions;
    const restoreFormState = () => {
      this.showActions = defaultShowActions;
      this.form.enable({ emitEvent: false });
      this.form.get('email')?.disable({ emitEvent: false });
      this.form.get('countryCode')?.disable({ emitEvent: false });
      this.form.get('phone')?.disable({ emitEvent: false });
      this.applyRoleSelectionRules(this.rolesFormArray.value as boolean[]);
      if (this.keepCompanyDisabled) {
        this.form.get('company')?.disable({ emitEvent: false });
      }
      this.formSubmitLoader = false;
    };

    if (isActionSubmit && this.checkRequiredDocuments()) {
      this.sharedService.toastMessage('ERROR', 'FILL_REQUIRED_FIELDS_DOCUMENTS', 'error', false);
      return;
    }

    const selectedRoles = this.rolesFormArray.value
      .map((checked: boolean, i: number) => checked ? ROLES[i].value : null)
      .filter((v: any) => v !== null);

    const request = new LinkMemberRequest();
    request.body.DCMethod = isActionSubmit ? "SUBMIT" : "SAVEASDRAFT"; //SUBMIT 
    request.body.DCLoginName = this.userLoginName;
    request.body.DCLanguage = this.lang == 'ar' ? 'ARA' : 'ENU';
    request.body.DCIncomingHierarchy.MessageId = Utils.generateUniqueId();

    let srData = request.body.DCIncomingHierarchy["ListOfDC Community Portal SR Upsert IO"]["DC Community Portal SR"]
    srData['SR Number'] = this.srNumber;
    srData["CSN"] = this.userCSN;
    srData["SR Type"] = ServiceName.USER_MANAGEMENT;
    srData["Sub Type"] = ServiceName.USER_ACCESS;
    srData["DC Payment Method"] = this.lang == 'ar' ? FieldValue.PAYMENT_METHOD_ONLINE_AR : FieldValue.PAYMENT_METHOD_ONLINE_EN;
    srData["DC Channel"] = this.lang == 'ar' ? FieldValue.CHANEL_ONLINE_AR : FieldValue.CHANEL_ONLINE_EN;
    srData["DC Link CSN"] = this.form.getRawValue().company; //this.selectedCompany.CSN;
    srData["DC Link UserID"] = this.userLoginName;
    srData['DC Link Mem Answer1'] = selectedRoles.includes("Owner") ? "Y" : "N";
    srData['DC Link Mem Answer2'] = selectedRoles.includes("Manager") ? "Y" : "N";
    srData['DC Link Mem Answer3'] = selectedRoles.includes("Representative") ? "Y" : "N";
    srData['DC Holder Mobile'] = this.form.getRawValue().officePhone ? `${this.form.getRawValue().officePhoneCountryCode || '971'}${this.form.getRawValue().officePhone}` : '';
    if (isActionSubmit) {
      this.showActions = false;
      this.form.disable({ emitEvent: false });
    }
    this.formSubmitLoader = true;
    this.api.post(
      ApiRoot.ATA_DC_SERVICE_REQUEST_INTEGRATION_API, // Use the correct API root for Link Member
      ApiEndPoints.ATA_MANAGE_SERVICE_REQUEST,
      request
    ).subscribe({
      // This method is used to handle the next callback using response
      next: (response: any) => {
        if (response['Error Code'] == null && response["DCSRNumber"]) {
          this.isSaved = true;
          this.srNumber = response["DCSRNumber"];
          sessionStorage.setItem("srNumber", response["DCSRNumber"]);
          if (!isActionSubmit) {
            this.openFolder();
            // this.sharedService.toastMessage('SUCCESS', 'SUCCESS', 'success');
            restoreFormState();
            return;
          }

          let lang: any = this.sharedService.getCurrentLang();
          const { service, serviceAr } = Utils.getServiceLabels(ServiceName.USER_ACCESS, lang);
          let srDetailObj = {
            serviceSubTypeEn: service,
            serviceSubTypeAr: serviceAr,
            mainService: ServiceName.USER_MANAGEMENT,
            service: ServiceName.USER_ACCESS,
            IOName: SrIOName.LINK_MEMBER,
            srNumber: this.srNumber,
            totalSteps: 4,
            step: 3
          }
          this.sharedService.setSrResponse(srDetailObj);
          this.sharedService.toastMessage('SUCCESS', 'REQUEST_SUBMITTED', 'success');
          this.router.navigate([`${RoutesPath.SERVICE}/${RoutesPath.SUCCESS_PAGE + "/" + this.srNumber}`]);
          restoreFormState();
        } else {
          restoreFormState();
          if (response['Error Code'] == 'DC-VAL-ERR' && response["Error Message"] != null) {
            this.errorMsg = response['Error Message'];
          } else {
            this.technicalErrorMsg = true;
          }
          // this.sharedService.toastMessage('ERROR', '', 'error', response["Error Message"]);
        }
      },
      error: () => {
        restoreFormState();
      }
    })
  }

  // This method is used to remind later
  remindLater() {
    this.router.navigate([RoutesPath.DASHBOARD]);
  }

  linkedUsers: AccountUser[] = [];
  otpModal: boolean = false;
  // This method is used to get Member linked users by CSN
  getLinkedUsers() {
    if (!this.form.getRawValue().company) {
      return;
    }
    if (this.userCSN == this.form.getRawValue().company) {
      this.sharedService.toastMessage('info', 'SELECTED_MEMBER_EXIST', 'error', true)
      return;
    }
    if (!this.srNumber && this.form.getRawValue().company) {
      this.upsertLinkMemberSr(false);
      return;
    }
    return;
    let requestPayload = {
      "body": {
        "ProcessName": "DC Get Account Contacts WF",
        "DC Channel": this.lang == 'ar' ? FieldValue.CHANEL_ONLINE_AR : FieldValue.CHANEL_ONLINE_EN,
        "DC Method": DcMethodValues.ADD_BUSINESS,
        "CSN": this.form.getRawValue().company
      }
    }
    this.api.post(ApiRoot.DCCICPIntegration, ApiEndPoints.RUN_PROCESS, requestPayload)
      .subscribe({
        // This method is used to handle the next callback using response
        next: (response: any) => {
          if (response["Error Code"] == "" && response["Error Message"] == "") {
            if (response.SiebelMessage?.["Account"] && response.SiebelMessage?.["Account"]?.["Contact"]) {
              this.linkedUsers = Utils.checkIsArray(response.SiebelMessage?.["Account"]?.["Contact"]);
            } else {
              // this.otpText = "";
              // this.otpModal = true;
            }
          }
        },
        // This method is used to handle the error callback using err
        error: (err: any) => {
        }
      });
  }

  // This method is used to approve reject request using type and access request
  approveRejectRequest(type: string, accessRequest: any) {
    this.formSubmitLoader = true;
    const request = new UpsertRequestModel();
    request.body.DCMethod = type == 'reject' ? "REJECT" : "APPROVE";
    request.body.DCLoginName = this.sharedService.getUserProfile()['Login Name'];
    request.body.DCLanguage = this.lang == 'ar' ? 'ARA' : 'ENU';
    request.body.DCIncomingHierarchy.MessageId = Utils.generateUniqueId();
    request.body.DCIncomingHierarchy.IntObjectName = "DC Community Portal SR Upsert IO";

    let srData = request.body.DCIncomingHierarchy["ListOfDC Community Portal SR Upsert IO"]["DC Community Portal SR"]
    srData['SR Number'] = this.srNumber;
    srData["SR Type"] = ServiceName.USER_MANAGEMENT;
    srData["Sub Type"] = ServiceName.USER_ACCESS;
    srData["CSN"] = this.requestDetail?.['DC Link CSN'];

    this.api.post(
      ApiRoot.ATA_DC_SERVICE_REQUEST_INTEGRATION_API, // Use the correct API root for Link Member
      ApiEndPoints.ATA_MANAGE_SERVICE_REQUEST,
      request
    ).subscribe({
      // This method is used to handle the next callback using response
      next: (response: any) => {
        if (response['Error Code'] == null) {
          this.sharedService.toastMessage('SUCCESS', 'SUCCESS', 'success');
          this.router.navigate([`${RoutesPath.DASHBOARD}`]);
          this.formSubmitLoader = false;
        } else {
          this.sharedService.toastMessage('ERROR', '', 'error', response["Error Message"]);
        }
      },
      // This method is used to handle the error callback using err
      error: (err: any) => {
        this.formSubmitLoader = false;
      }
    });
  }
  // This method is used to validate otp
  validateOtp() {
    if (!this.otpText || !this.otpText.trim()) {
      this.otpErrorKey = 'VALIDATION_MSG.FIELD_REQUIRED';
      return;
    }

    this.otpErrorKey = null;
    this.otpModal = false;
  }
  // This method is used to resend otp
  resendOtp() {
    this.otpErrorKey = null;
  }

  onOtpChange(value: string) {
    if (this.otpErrorKey && value?.trim()) {
      this.otpErrorKey = null;
    }
  }

  checkRequiredDocuments(): boolean {
    if (!this.mainFromSectionDocs || this.mainFromSectionDocs.length === 0) {
      return false;
    }

    const fileCmps = this.fileComponents && typeof this.fileComponents.toArray === 'function'
      ? this.fileComponents.toArray()
      : [];

    if (fileCmps.length < this.mainFromSectionDocs.length) {
      return false;
    }

    let hasMissing = false;
    for (let index = 0; index < this.mainFromSectionDocs.length; index++) {
      const doc = this.mainFromSectionDocs[index];
      const cmp = fileCmps[index];
      if (!cmp) {
        continue;
      }

      if (typeof cmp.checkMissing === 'function' && cmp.checkMissing(!!doc?.mandatory)) {
        hasMissing = true;
      }
    }

    return hasMissing;
  }

  openFolder() {
    const isLabels = !this.srNumber;

    this.sharedService.getDocuments(this.srNumber, DocumentServiceType.UM_UA, isLabels)
      .subscribe({
        next: (response: any) => {
          if (response.success == true) {
            this.srFolder = response.data ? response.data : response;
            this.mainFromSectionDocs = this.srFolder.allowedDocumentTypes?.filter((d: any) => !d.forUISection) ?? [];
          }
        }
      });
  }
  ngOnDestroy(): void {
    sessionStorage.removeItem("srNumber");
  }
}
